import { useEffect, useMemo, useState } from "react"
import { useInertiaCable } from "@inertia-cable/react"
import { router } from "@inertiajs/react"
import { AppShell } from "@/components/layout/app-shell"
import { PageHeader } from "@/components/layout/page-header"
import { TicketsToolbar } from "@/components/domain/tickets/tickets-toolbar"
import { TicketsFilterBar, type TicketsFilterMeta } from "@/components/domain/tickets/tickets-filter-bar"
import { TicketsTable } from "@/components/domain/tickets/tickets-table"
import { loadVisibleColumns, saveVisibleColumns, type ColumnKey } from "@/components/domain/tickets/columns"
import type { Ticket } from "@/types/ticket"

type Props = {
  tickets: Ticket[]
  moreAvailable: boolean
  filterStatus: string[]
  filterAssignedTo: string | null
  filterPriority: string[]
  filterQueue: string
  filterTags: string[]
  filterQuery: string
  filterSlaBreached: "fr" | "idle" | "res" | "any" | null
  filterSlaBreachedSince: string | null
  sortBy: string | null
  sortDir: "asc" | "desc"
  filterMeta: TicketsFilterMeta
  tickets_stream: string
}

export default function TicketsIndex(props: Props) {
  const {
    tickets,
    moreAvailable,
    filterStatus,
    filterAssignedTo,
    filterPriority,
    filterQueue,
    filterTags,
    filterQuery,
    filterSlaBreached,
    filterSlaBreachedSince,
    sortBy,
    sortDir,
    filterMeta,
    tickets_stream,
  } = props

  // Live updates: any AgentConvo state flip OR portal-visible AgentMessage
  // create on this company refreshes the queue. Pulling `ticketsNav` (a
  // shared inertia prop) keeps the global sidebar badges in sync alongside
  // the list.
  useInertiaCable(tickets_stream, {
    only: ["tickets", "ticketsNav"],
    debounce: 250,
  })

  // Column visibility lives in localStorage — survives reloads but doesn't
  // belong in the URL.
  const [visibleColumns, setVisibleColumns] = useState<ColumnKey[]>(loadVisibleColumns)
  useEffect(() => { saveVisibleColumns(visibleColumns) }, [visibleColumns])

  // All filter / sort / search state lives in URL params. Each handler
  // builds the next URL and asks Inertia to partially re-render the
  // `tickets` prop (which the controller re-derives).
  function visit(updates: Record<string, string | string[] | null>) {
    const url = new URL(window.location.href)
    for (const [key, value] of Object.entries(updates)) {
      url.searchParams.delete(key)
      url.searchParams.delete(`${key}[]`)
      if (value == null) continue
      if (Array.isArray(value)) {
        for (const v of value) {
          if (v) url.searchParams.append(`${key}[]`, v)
        }
      } else if (value !== "") {
        url.searchParams.set(key, value)
      }
    }
    router.visit(`${url.pathname}?${url.searchParams.toString()}`, {
      preserveState: true,
      preserveScroll: true,
      only: ["tickets", "moreAvailable", "filterStatus", "filterAssignedTo", "filterPriority", "filterQueue", "filterTags", "filterQuery", "filterSlaBreached", "filterSlaBreachedSince", "sortBy", "sortDir"],
    })
  }

  const subtitle = useMemo(() => {
    if (tickets.length === 0) return "No tickets in current view"
    return `${tickets.length} ticket${tickets.length === 1 ? "" : "s"}${moreAvailable ? "+ — narrow filters to see more" : ""} in current view`
  }, [tickets.length, moreAvailable])

  return (
    <AppShell>
      <PageHeader
        eyebrow="Tickets"
        title="Support queue"
        subtitle={subtitle}
      />

      <div className="space-y-3">
        <TicketsToolbar
          query={filterQuery}
          onQueryChange={(q) => visit({ q })}
          sortBy={sortBy}
          sortDir={sortDir}
          onSortChange={(s, d) => visit({ sort: s, dir: d })}
          visibleColumns={visibleColumns}
          onVisibleColumnsChange={setVisibleColumns}
        />

        <TicketsFilterBar
          filterMeta={filterMeta}
          queue={filterQueue}
          priority={filterPriority}
          tags={filterTags}
          status={filterStatus}
          assignedTo={filterAssignedTo ?? ""}
          slaBreached={filterSlaBreached}
          slaBreachedSince={filterSlaBreachedSince}
          onQueueChange={(v) => visit({ queue: v === "all" ? null : v })}
          onPriorityChange={(v) => visit({ priority: v })}
          onTagsChange={(v) => visit({ tags: v })}
          onStatusChange={(v) => visit({ status: v })}
          onAssignedToChange={(v) => visit({ assigned_to: v || null })}
          onClearSlaBreached={() => visit({ sla_breached: null, since: null })}
          onClear={() => router.visit("/tickets", { preserveScroll: true })}
        />

        <TicketsTable
          tickets={tickets}
          visibleColumns={visibleColumns}
          sortBy={sortBy}
          sortDir={sortDir}
          onSortChange={(s, d) => visit({ sort: s, dir: d })}
        />
      </div>
    </AppShell>
  )
}
